import { useState, useMemo, useEffect, useCallback } from "react";
import { Search, RefreshCw, Shield, Key, Lock, Activity, ChevronsUpDown, Check, Cloud, TrendingDown, TrendingUp, ShieldCheck, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { SiGooglecloud, SiAmazon, SiKubernetes } from "react-icons/si";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Card, CardContent } from "../../ui/card";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { getAWSCloudIAMRole, getAWSCloudOIDC, getAWSCloudSecrets, getAWSKubernetesSecrets, getAWSKubernetesServiceAccounts, getAWSKubernetesTokens, getAzureCloudSecrets, getAzureCloudServiceAccounts, getAzureCloudTokens, getAzureKubernetesSecrets, getAzureKubernetesServiceAccounts, getAzureKubernetesTokens, getGCPCloudSecrets, getGCPCloudServiceAccounts, getGCPCloudTokens, getGCPKubernetesSecrets, getGCPKubernetesServiceAccounts, getGCPKubernetesTokens } from "./Data";
import { DetailsDialog } from "../Cloud/DetailsDialog";
import CloudAccountList from "../CloudAccountList";
import { myLocalStorage } from "../../StorageHelper";
import { useNavigate } from "react-router-dom";
import { VscAzure } from "react-icons/vsc";
import { FaGoogle } from "react-icons/fa6";
import TenantLists from "../TenantLists";
import useCloudSyncStore from "../../../services/useCloudSyncStore";
import CloudFilters from "../CloudFilters";
import AlertTrendPanel from "../Cloud/AlertTrendPanel";
import axios from "axios";
import { NGROK } from "../../../APIs";


// Activity gradient color based on count
const getActivityColor = (count: number) => {
    if (count < 10) return "bg-gradient-to-r from-yellow-400 to-yellow-500";
    if (count <= 50) return "bg-gradient-to-r from-orange-400 to-orange-500";
    return "bg-gradient-to-r from-orange-600 to-orange-700";
};

const getActivityIntensity = (count: number) => {
    if (count < 10) return Math.min((count / 10) * 100, 100);
    if (count <= 50) return Math.min(((count - 10) / 40) * 100, 100);
    return 100;
};

// Activity Bar Component
function ActivityBar({ count, className = "" }: { count: number; className?: string }) {
    const intensity = getActivityIntensity(count);
    const colorClass = getActivityColor(count);

    return (
        <div className={`relative h-2 bg-muted rounded-full overflow-hidden ${className}`}>
            <div
                className={`h-full ${colorClass} rounded-full transition-all`}
                style={{ width: `${intensity}%` }}
            />
        </div>
    );
}

const getDisplayValue = (value: any, fallback = "N/A") => {
    if (value === null || value === undefined || value === "") return fallback;
    return String(value);
};

const renderSecretCardMeta = (secret: any) => {
    const owner = getDisplayValue(secret.owner || secret.projectId || secret.tenantName);
    const createdAt = getDisplayValue(secret.createdAt);
    const replication = getDisplayValue(secret.replicationType);

    return (
        <div className="flex flex-wrap gap-1.5 text-[11px]">
            <span className="inline-flex items-center rounded-md bg-muted/70 px-2 py-1 text-muted-foreground">
                <span className="mr-1 font-medium text-foreground">Owner:</span> {owner}
            </span>
            <span className="inline-flex items-center rounded-md bg-muted/70 px-2 py-1 text-muted-foreground">
                <span className="mr-1 font-medium text-foreground">Created:</span> {createdAt}
            </span>
            <span className="inline-flex items-center rounded-md bg-muted/70 px-2 py-1 text-muted-foreground">
                <span className="mr-1 font-medium text-foreground">Replication:</span> {replication}
            </span>
        </div>
    );
};

const CloudIcon = ({ provider }: { provider: string }) => {
    if (provider === 'gcp') {
        return <FaGoogle className="w-8 h-8 text-primary" />;
    } else if (provider === 'aws') {
        return <SiAmazon className="w-8 h-8 text-primary" />;
    } else {
        return <VscAzure className="w-8 h-8 text-primary" />;
    }
};


/** Filter riskTags: remove alert-count tags, and if both "X name" and "+X name" exist, only keep "+X name" */
const filterRiskTags = (tags: string[]): string[] => {
    const noAlerts = tags.filter(t => !/\d+\s*alerts?$/i.test(t));
    return noAlerts.filter(tag => {
        if (tag.startsWith("+")) return true;
        const plusVersion = `+${tag}`;
        return !noAlerts.includes(plusVersion);
    });
};

interface CloudNHIPageProps {
    cloudProvider: string;
}

export default function CloudNHIPage({ cloudProvider }: CloudNHIPageProps) {
    const navigate = useNavigate();
    const [selectedTenant, setSelectedTenant] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [platformTab, setPlatformTab] = useState("cloud");
    const [activeTab, setActiveTab] = useState("service-accounts");
    const [riskFilter, setRiskFilter] = useState<"all" | "improved" | "worsened" | "neutral">("worsened");
    const [syncing, setSyncing] = useState(false);
    const [securityProgress, setSecurityProgress] = useState<any>(null);

    const [cloudServiceAccounts, setCloudServiceAccounts] = useState<any[]>([]);
    const [cloudTokens, setCloudTokens] = useState<any[]>([]);
    const [cloudSecrets, setCloudSecrets] = useState<any[]>([]);
    const [kubernetesServiceAccounts, setKubernetesServiceAccounts] = useState<any[]>([]);
    const [kubernetesTokens, setKubernetesTokens] = useState<any[]>([]);
    const [kubernetesSecrets, setKubernetesSecrets] = useState<any[]>([]);
    const [cloudProviderChanges, setCloudProviderChanges] = useState<any>();
    const [loading, setLoading] = useState(false);
    const { syncStatus } = useCloudSyncStore((state) => ({
        syncStatus: state.syncStatus[cloudProvider],
    }));

    const handleSync = () => {
        setSyncing(true);
        setTimeout(() => setSyncing(false), 2000);
    };

    useEffect(() => {
        if (!cloudProviderChanges) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                if (cloudProvider === "gcp") {
                    setCloudServiceAccounts(await getGCPCloudServiceAccounts());
                    setCloudTokens(await getGCPCloudTokens());
                    setCloudSecrets(await getGCPCloudSecrets());
                    setKubernetesServiceAccounts(await getGCPKubernetesServiceAccounts());
                    setKubernetesTokens(await getGCPKubernetesTokens());
                    setKubernetesSecrets(await getGCPKubernetesSecrets());
                } else if (cloudProvider.toLowerCase() === "aws") {
                    setCloudServiceAccounts(await getAWSCloudIAMRole());
                    setCloudTokens(await getAWSCloudOIDC());
                    setCloudSecrets(await getAWSCloudSecrets());
                    setKubernetesServiceAccounts(await getAWSKubernetesServiceAccounts());
                    setKubernetesTokens(await getAWSKubernetesTokens());
                    setKubernetesSecrets(await getAWSKubernetesSecrets());
                } else if (cloudProvider === "azure") {
                    setCloudServiceAccounts(await getAzureCloudServiceAccounts());
                    setCloudTokens(await getAzureCloudTokens());
                    setCloudSecrets(await getAzureCloudSecrets());
                    setKubernetesServiceAccounts(await getAzureKubernetesServiceAccounts());
                    setKubernetesTokens(await getAzureKubernetesTokens());
                    setKubernetesSecrets(await getAzureKubernetesSecrets());
                } else {
                    // Clear all if unknown provider
                    setCloudServiceAccounts([]);
                    setCloudTokens([]);
                    setCloudSecrets([]);
                    setKubernetesServiceAccounts([]);
                    setKubernetesTokens([]);
                    setKubernetesSecrets([]);
                }
            } catch (err) {
                console.error("Error loading cloud data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cloudProvider, selectedTenant, cloudProviderChanges]);

    /** Map API riskChangeCategory to local status */
    const mapRiskCategory = (cat: string | null | undefined): "improved" | "worsened" | "neutral" => {
        switch (cat) {
            case "RISK_INCREASED": return "worsened";
            case "RISK_REDUCED": return "improved";
            case "NEUTRAL": return "neutral";
            default: return "worsened";
        }
    };

    const fetchSecurityProgress = useCallback(async () => {
        try {
            const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || myLocalStorage.getItem("latestTenant");
            if (!tenantName) return;
            const cp = cloudProvider?.toLowerCase();
            let url = "";
            let params: any = { tenantName };
            if (cp === "gcp") {
                const projectId = myLocalStorage.getItem("gcpSelectedProjectId");
                if (!projectId) return;
                url = `${NGROK}/api/gcp/security-progress`;
                params.projectId = projectId;
            } else if (cp === "aws") {
                const accountId = myLocalStorage.getItem("selectedAwsAccount");
                if (!accountId) return;
                url = `${NGROK}/api/aws/security-progress`;
                params.accountId = accountId;
            } else if (cp === "azure") {
                const sub = myLocalStorage.getItem("latestSubList");
                const subscriptionId = sub?.azureSubscriptionId;
                if (!subscriptionId) return;
                url = `${NGROK}/api/azure/v1/security-progress`;
                params.subscriptionId = subscriptionId;
            }
            if (!url) return;
            const { data } = await axios.get(url, { params });
            // Store the nhis-specific sub-object for the NHI page
            setSecurityProgress(data?.overallSecurityProgress?.nhis || null);
        } catch (err) {
            console.error("Error loading security progress:", err);
            setSecurityProgress(null);
        }
    }, [cloudProvider]);

    useEffect(() => {
        if (!cloudProviderChanges) return;
        fetchSecurityProgress();
    }, [cloudProviderChanges, fetchSecurityProgress]);

    const analyzedServiceAccounts = useMemo(() => {
        return cloudServiceAccounts.map(sa => ({
            ...sa,
            status: mapRiskCategory(sa.riskChangeCategory),
        }));
    }, [cloudServiceAccounts]);

    const improved = useMemo(() => analyzedServiceAccounts.filter(a => a.status === "improved"), [analyzedServiceAccounts]);
    const worsened = useMemo(() => analyzedServiceAccounts.filter(a => a.status === "worsened"), [analyzedServiceAccounts]);
    const neutral = useMemo(() => analyzedServiceAccounts.filter(a => a.status === "neutral"), [analyzedServiceAccounts]);

    // Use security-progress API for the overall bar + hero cards
    const sp = securityProgress;
    const displayTotal = sp?.totalEntities ?? cloudServiceAccounts.length;
    const displayImproved = sp?.riskReducedCount ?? improved.length;
    const displayWorsened = sp?.riskIncreasedCount ?? worsened.length;
    const displayNeutral = sp?.neutralCount ?? neutral.length;

    const improvePct = sp?.greenBarPercentage ?? (displayTotal ? (displayImproved / displayTotal) * 100 : 0);
    const worsenPct = sp?.redBarPercentage ?? (displayTotal ? (displayWorsened / displayTotal) * 100 : 0);
    const neutralPct = displayTotal ? Math.max(100 - improvePct - worsenPct, 0) : 0;

    const filteredCloudServiceAccounts = useMemo(() => {
        let list = analyzedServiceAccounts;
        if (riskFilter !== "all") {
            list = list.filter(a => a.status === riskFilter);
        }
        if (searchQuery) {
            const sq = searchQuery.toLowerCase();
            list = list.filter(
                (sa) =>
                    sa.email?.toLowerCase().includes(sq) ||
                    sa.algorithm?.toLowerCase().includes(sq) ||
                    sa.name?.toLowerCase().includes(sq) ||
                    sa.roleName?.toLowerCase().includes(sq)
            );
        }
        return list;
    }, [analyzedServiceAccounts, riskFilter, searchQuery]);

    const filteredK8sServiceAccounts = kubernetesServiceAccounts.filter(
        (sa) =>
            sa.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            sa.namespace?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredCloudTokens = cloudTokens.filter(
        (token) =>
            token.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            token.type?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredCloudSecrets = cloudSecrets.filter(
        (secret) =>
            secret.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            secret.type?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredK8sTokens = kubernetesTokens.filter(
        (token) =>
            token.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            token.type?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredK8sSecrets = kubernetesSecrets.filter(
        (secret) =>
            secret.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            secret.type?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleTenantChange = (tenant: string) => {
        setSelectedTenant(tenant);
    };

    const handleCloudProvider = (value: any) => {
        setCloudProviderChanges(value);
    }

    const getK8sNHILink = (item: any, activeTab: any) => {
        const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
        const cloudType = cloudProvider;

        let type = "K8SERVICE"
        if (activeTab === "tokens") type = "K8TOKEN"
        else if (activeTab === "secrets") type = "K8SECRET";
        else type = "K8SERVICE"

        // Determine correct URL based on cloud type (Azure/GCP currently supported)
        const url =
            cloudType === "AZURE"
                ? "/azure-directory/nhi/service-account-details"
                : "/gcp/nhi/service-account-details";

        // Build state object dynamically depending on item type
        const stateObj: any = {
            tenantName: latestTenant,
            cloudType,
            cloudNHIType: type,
            secretName: item?.name,
            nameSpace: item?.namespace || item?.nameSpace,
            from: "NonHumanIdentitiesTable",
        };

        // Attach the specific item details
        if (type === "K8SECRET") {
            stateObj.secretDetails = item;
        } else if (type === "K8SERVICE") {
            stateObj.serviceDetails = item;
        } else if (type === "K8TOKEN") {
            stateObj.tokenDetails = item;
        }

        // Save to local storage (for persistence)
        myLocalStorage.setItem("nhi-details", JSON.stringify(stateObj));

        // Return object (if used for navigation in other parts)
        return {
            pathname: url,
            state: stateObj,
        };
    };

    const getNHILink = (item: any, type: string, cloudProvider: string) => {
        const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
        const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");
        const selectedSubListStr = myLocalStorage.getItem("latestSubList");
        const latestprojectId = myLocalStorage.getItem("gcpSelectedProjectId");

        const cloudType = cloudProvider?.toUpperCase();

        const pathname =
            cloudType === "AWS"
                ? "/aws/nhi/service-account-details"
                : cloudType === "AZURE"
                    ? "/azure-directory/nhi/service-account-details"
                    : "/gcp/nhi/service-account-details";

        const stateObj: any = {
            tenantName: latestTenant,
            cloudType,
            from: "NonHumanIdentitiesTable",
        };

        switch (type.toUpperCase()) {


            case "SERVICE_ACCOUNT":
                stateObj.item = item;
                stateObj.cloudNHIType = "SERVICE_ACCOUNT";
                stateObj.serviceAccountEmail = item.email;
                stateObj.projectId = latestprojectId || item.projectId || null;
                stateObj.accountId = selectedAwsAccount || item.accountId || null;
                stateObj.syncedAt =
                    item.serviceDetails?.syncedAt ||
                    item.syncedAt ||
                    item.serviceDetails?.createDate ||
                    item.createDate ||
                    item.validityPeriod ||
                    "";
                stateObj.roles =
                    item.roles ||
                    item.serviceroles ||
                    item.serviceDetails?.roles ||
                    item.serviceDetails?.serviceroles ||
                    [];
                stateObj.roleDetails =
                    item.iamRoleDetails ||
                    item.roleDetails ||
                    item.serviceDetails ||
                    null;
                stateObj.principalId = item.principalId || "";
                stateObj.sa = item;
                stateObj.serviceDetails = item.serviceDetails || null;
                stateObj.createdAt =
                    item.serviceDetails?.createDate ||
                    item.createDate ||
                    item.serviceDetails?.syncedAt ||
                    item.syncedAt ||
                    item.validityPeriod ||
                    "";
                myLocalStorage.setItem("nhi-details", JSON.stringify(stateObj));

                break;

            case "SECRET":
                myLocalStorage.setItem("nhi-details", JSON.stringify(item.secretDetails));
                stateObj.cloudNHIType = "SECRET";
                stateObj.secretName =
                    cloudType === "AWS" ? item?.secretArn : item?.name;
                stateObj.cloudSecret = item;
                stateObj.createdAt = item.createdAt
                break;


            case "TOKEN":
                myLocalStorage.setItem("nhi-details", JSON.stringify(item.tokenDetails));
                stateObj.cloudNHIType = "TOKEN";
                stateObj.secretName = item?.secretName || item?.name;
                stateObj.cloudTokenDetails = item;
                stateObj.createdAt = item.createdAt
                break;


            case "OIDC":
                myLocalStorage.setItem("nhi-details", JSON.stringify(item.oidcDetails));
                stateObj.cloudNHIType = "OIDC";
                stateObj.oidcARN = item?.name;
                stateObj.providerArn = item?.name;
                stateObj.oidc = item;
                stateObj.createDate = item.createdAt
                break;

            default:
                console.warn(`Unknown NHI type passed: ${type}`);
                break;
        }


        return {
            pathname,
            state: stateObj,
        };
    };


    return (
        <div className="h-full flex flex-col bg-background">
            <div className="p-6">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <CloudIcon provider={cloudProvider} />
                    <div className="flex-1">
                        <h1 className="text-2xl font-semibold text-foreground"> {cloudProvider.toUpperCase()} Non-Human Identities</h1>
                        <p className="text-sm text-muted-foreground">Manage service accounts, tokens, and secrets</p>
                    </div>
                </div>

                {/* Cloud Filters */}
                <CloudFilters
                    cloudProvider={cloudProvider}
                    onTenantChange={handleTenantChange}
                    onCloudAccountChange={handleCloudProvider}
                    isAdmin={true}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    searchPlaceholder="Search entities…"
                    onSync={handleSync}
                    syncDisabled={syncStatus}
                    syncLoading={syncStatus}
                />

                {/* Navigation Control Center */}
                <Card className="mb-6 bg-gradient-to-r from-background to-muted/10 border-border/50 shadow-sm overflow-hidden">
                    <div className="flex flex-col md:flex-row md:items-center justify-between p-3 gap-6">

                        {/* Left: Entity Tabs */}
                        <div className="flex items-center">
                            <div className="cs-tabs !m-0 !w-auto">
                                <button
                                    onClick={() => setActiveTab("service-accounts")}
                                    className={`cs-tab ${activeTab === "service-accounts" ? "active" : ""}`}
                                    data-testid="tab-service-accounts"
                                >
                                    <Shield className="w-4 h-4" />
                                    {platformTab === "cloud" && cloudProvider.toLowerCase() === "aws" ? "IAM Role" : "Service Accounts"}
                                </button>
                                <button
                                    onClick={() => setActiveTab("tokens")}
                                    className={`cs-tab ${activeTab === "tokens" ? "active" : ""}`}
                                    data-testid="tab-tokens"
                                >
                                    <Key className="w-4 h-4" />
                                    {platformTab === "cloud" && cloudProvider.toLowerCase() === "aws" ? "OIDC" : "Tokens"}
                                </button>
                                <button
                                    onClick={() => setActiveTab("secrets")}
                                    className={`cs-tab ${activeTab === "secrets" ? "active" : ""}`}
                                    data-testid="tab-secrets"
                                >
                                    <Lock className="w-4 h-4" />
                                    Secrets
                                </button>
                            </div>
                        </div>

                        {/* Right: Platform Tabs */}
                        <div className="flex items-center">
                            <div className="cs-tabs !m-0 !w-auto">
                                <button
                                    onClick={() => setPlatformTab("cloud")}
                                    className={`cs-tab ${platformTab === "cloud" ? "active" : ""}`}
                                    data-testid="tab-cloud-nhi"
                                >
                                    {cloudProvider === 'gcp' ? (
                                        <FaGoogle className="w-4 h-4" />
                                    ) : cloudProvider === 'aws' ? (
                                        <SiAmazon className="w-4 h-4" />
                                    ) : (
                                        <VscAzure className="w-4 h-4" />
                                    )}
                                    Cloud NHI
                                </button>
                                <button
                                    onClick={() => setPlatformTab("kubernetes")}
                                    className={`cs-tab ${platformTab === "kubernetes" ? "active" : ""}`}
                                    data-testid="tab-kubernetes-nhi"
                                >
                                    <SiKubernetes className="w-4 h-4" />
                                    Kubernetes NHI
                                </button>
                            </div>
                        </div>

                    </div>
                </Card>

                {/* Cloud NHI Content */}
                {platformTab === "cloud" && (
                    <div className="space-y-4">

                        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">

                            <TabsContent value="service-accounts" className="space-y-2">
                                <div className="mb-6 space-y-6">
                                    {/* Overall Security Progress */}
                                    <div className="cs-progress-hero" style={{ margin: 0 }}>
                                        <div className="cs-progress-details">
                                            <div className="cs-progress-title-row">
                                                <h3 className="cs-progress-main-title">Overall Security Progress</h3>
                                                <span className="cs-progress-subtitle">{displayTotal} entities analyzed</span>
                                            </div>

                                            <div className="cs-bar-wrap">
                                                <div className="cs-bar-track">
                                                    <div className="cs-bar-fill improved" style={{ width: `${improvePct}%` }} />
                                                    <div className="cs-bar-fill worsened" style={{ width: `${worsenPct}%` }} />
                                                    <div className="cs-bar-fill unchanged" style={{ width: `${neutralPct}%` }} />
                                                </div>
                                            </div>

                                            <div className="cs-bar-legend">
                                                <div className="cs-bar-legend-item">
                                                    <div className="cs-bar-legend-dot improved" />
                                                    <TrendingDown className="cs-bar-legend-icon improved" />
                                                    <span className="cs-bar-legend-count">{displayImproved}</span>
                                                    <span className="cs-bar-legend-label">Risk Reduced</span>
                                                </div>
                                                <div className="cs-bar-legend-item">
                                                    <div className="cs-bar-legend-dot worsened" />
                                                    <TrendingUp className="cs-bar-legend-icon worsened" />
                                                    <span className="cs-bar-legend-count">{displayWorsened}</span>
                                                    <span className="cs-bar-legend-label">Risk Increased</span>
                                                </div>
                                                <div className="cs-bar-legend-item">
                                                    <div className="cs-bar-legend-dot unchanged" />
                                                    <ShieldCheck className="cs-bar-legend-icon unchanged" />
                                                    <span className="cs-bar-legend-count">{displayNeutral}</span>
                                                    <span className="cs-bar-legend-label">Neutral</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <AlertTrendPanel cloudProvider={cloudProvider} />

                                    {/* Quick Info Cards */}
                                    <div className="cs-hero" style={{ margin: 0, marginTop: "16px" }}>
                                        <div className="cs-hero-card">
                                            <div className="cs-hero-icon blue"><Shield className="w-6 h-6" /></div>
                                            <div className="cs-hero-info">
                                                <span className="cs-hero-value">{displayTotal}</span>
                                                <span className="cs-hero-label">Total Services</span>
                                            </div>
                                        </div>
                                        <div className="cs-hero-card">
                                            <div className="cs-hero-icon green"><TrendingDown className="w-6 h-6" /></div>
                                            <div className="cs-hero-info">
                                                <span className="cs-hero-value">{displayImproved}</span>
                                                <span className="cs-hero-label">Risk Reduced</span>
                                            </div>
                                        </div>
                                        <div className="cs-hero-card">
                                            <div className="cs-hero-icon red"><TrendingUp className="w-6 h-6" /></div>
                                            <div className="cs-hero-info">
                                                <span className="cs-hero-value">{displayWorsened}</span>
                                                <span className="cs-hero-label">Risk Increased</span>
                                            </div>
                                        </div>
                                        <div className="cs-hero-card">
                                            <div className="cs-hero-icon gray"><ShieldCheck className="w-6 h-6" /></div>
                                            <div className="cs-hero-info">
                                                <span className="cs-hero-value">{displayNeutral}</span>
                                                <span className="cs-hero-label">Neutral</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status Filters */}
                                    <div className="cs-tabs">
                                        <button className={`cs-tab worsened-tab ${riskFilter === "worsened" ? "active" : ""}`} onClick={() => setRiskFilter("worsened")}>
                                            <TrendingUp className="w-4 h-4" /> Risk Increased <span className="cs-tab-count">{worsened.length}</span>
                                        </button>
                                        <button className={`cs-tab improved-tab ${riskFilter === "improved" ? "active" : ""}`} onClick={() => setRiskFilter("improved")}>
                                            <TrendingDown className="w-4 h-4" /> Risk Reduced <span className="cs-tab-count">{improved.length}</span>
                                        </button>
                                        <button className={`cs-tab unchanged-tab ${riskFilter === "neutral" ? "active" : ""}`} onClick={() => setRiskFilter("neutral")}>
                                            <ShieldCheck className="w-4 h-4" /> Neutral <span className="cs-tab-count">{neutral.length}</span>
                                        </button>
                                    </div>
                                </div>
                                {(loading || !cloudProviderChanges) && filteredCloudServiceAccounts.length === 0 ? (
                                    <div className="space-y-2">
                                        {[1, 2, 3, 4].map((i) => (
                                            <Card key={i} className="animate-pulse">
                                                <CardContent className="p-4 flex items-center gap-4">
                                                    <div className="w-8 h-8 rounded-full bg-muted/60 flex-shrink-0" />
                                                    <div className="flex-1 space-y-2">
                                                        <div className="h-4 bg-muted/60 rounded w-1/4" />
                                                        <div className="h-3 bg-muted/60 rounded w-1/2" />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                ) : filteredCloudServiceAccounts.length === 0 ? (
                                    <Card className="border-dashed">
                                        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                                            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                                <CloudIcon provider={cloudProvider} />
                                            </div>
                                            <h3 className="text-lg font-semibold mb-2">No {cloudProvider.toUpperCase()} services found</h3>
                                            <p className="text-sm text-muted-foreground max-w-md mb-6">
                                                You currently don't have any services account.
                                            </p>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    filteredCloudServiceAccounts.map((sa) => {
                                        const userLink = getNHILink(sa, "SERVICE_ACCOUNT", cloudProvider);

                                        return (
                                            <Card key={sa.id} className="hover-elevate cursor-pointer transition-transform hover:scale-[1.01]" data-testid={`card-sa-${sa.id}`}>
                                                <CardContent className="p-4">

                                                    <div className="flex items-start gap-3 block hover:no-underline">
                                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                            <Shield className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">

                                                            <h3 className="font-semibold text-sm truncate mb-2 block hover:no-underline" onClick={() => {
                                                                navigate(userLink.pathname, { state: userLink.state })

                                                            }}>{sa.email}</h3>
                                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                                <Badge variant="secondary" className="text-xs h-5">{sa.algorithm}</Badge>
                                                                <Badge variant="outline" className="text-xs h-5">{sa.managedType}</Badge>
                                                                {sa.riskTags && sa.riskTags.length > 0 && filterRiskTags(sa.riskTags)
                                                                    .map((tag: string, idx: number) => (
                                                                    <span key={idx} className="cs-card-tag added"><ArrowUpRight className="w-3 h-3" /> {tag}</span>
                                                                ))}
                                                            </div>
                                                            {sa.serviceroles && sa.serviceroles.length > 0 && (
                                                                <div className="flex flex-wrap items-center gap-1 mt-1">
                                                                    <span className="font-medium text-xs text-muted-foreground mr-1">Roles:</span>
                                                                    {sa.serviceroles.map((role: any, idx: any) => (
                                                                        <Badge key={idx} variant="outline" className="text-[10px] h-5">
                                                                            {role}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            <div className="text-xs text-muted-foreground">
                                                                <span className="font-medium">Key ID:</span> {sa.keyId}
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                            <div className="text-xs text-muted-foreground">Activity</div>
                                                            <ActivityBar count={sa.activity} className="w-20" />
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 pt-3 border-t flex justify-end">
                                                        <DetailsDialog data={sa} title={`Service Account: ${sa.email}`} />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )
                                    })
                                )}
                            </TabsContent>

                            <TabsContent value="tokens" className="space-y-2">
                                {filteredCloudTokens.length === 0 ? (
                                    <Card className="border-dashed">
                                        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                                            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                                <CloudIcon provider={cloudProvider} />
                                            </div>
                                            <h3 className="text-lg font-semibold mb-2">No {cloudProvider.toUpperCase()} entities found</h3>
                                            <p className="text-sm text-muted-foreground max-w-md mb-6">
                                                You currently don't have any entities for this account.
                                            </p>
                                            {/**
                                                                              * <Button variant="outline" onClick={() => navigate("/aws/users")} data-testid="button-go-to-resources">
                                                                               Go to Resources
                                                                             </Button>
                                                                              */}

                                        </CardContent>
                                    </Card>

                                ) : (
                                    filteredCloudTokens.map((token) => {
                                        let type = cloudProvider.toLowerCase() === "aws" ? "OIDC" : "TOKEN";
                                        let tokenLink = getNHILink(token, type, cloudProvider)

                                        return (
                                            <Card key={token.id} className="hover-elevate cursor-pointer transition-transform hover:scale-[1.01]" data-testid={`card-token-${token.id}`}>
                                                <CardContent className="p-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                            <Key className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1">

                                                                <h3 className="font-semibold text-sm block hover:no-underline" onClick={() =>
                                                                    navigate(tokenLink.pathname, { state: tokenLink.state })
                                                                } >{token.name}</h3>
                                                                <Badge variant="secondary" className="text-xs h-5">{token.type}</Badge>
                                                            </div>
                                                            <p className="text-xs font-mono text-muted-foreground mb-2">{token.keyId}</p>
                                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                                                <div>
                                                                    <span className="text-muted-foreground">Created:</span> {token.createdAt}
                                                                </div>
                                                                <div>
                                                                    <span className="text-muted-foreground">Expires:</span> {token.expiresAt}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                            <div className="text-xs text-muted-foreground">Activity</div>
                                                            <ActivityBar count={token.activity} className="w-20" />
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 pt-3 border-t flex justify-end">
                                                        <DetailsDialog data={token} title={`Token: ${token.name}`} />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )
                                    })
                                )}
                            </TabsContent>

                            <TabsContent value="secrets" className="space-y-2">
                                {filteredCloudSecrets.length === 0 ? (
                                    <Card className="border-dashed">
                                        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                                            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                                <CloudIcon provider={cloudProvider} />
                                            </div>
                                            <h3 className="text-lg font-semibold mb-2">No {cloudProvider.toUpperCase()} entities found</h3>
                                            <p className="text-sm text-muted-foreground max-w-md mb-6">
                                                You currently don't have any entities for this account.
                                            </p>
                                            {/**
                                                                              * <Button variant="outline" onClick={() => navigate("/aws/users")} data-testid="button-go-to-resources">
                                                                               Go to Resources
                                                                             </Button>
                                                                              */}

                                        </CardContent>
                                    </Card>
                                ) : (
                                    filteredCloudSecrets.map((secret) => {
                                        const secretLink = getNHILink(secret, "SECRET", cloudProvider);

                                        return (
                                            <Card
                                                key={secret.id}
                                                className="hover-elevate cursor-pointer transition-transform hover:scale-[1.01]"
                                                data-testid={`card-secret-${secret.id}`}
                                                onClick={() => navigate(secretLink.pathname, { state: secretLink.state })}
                                            >
                                                <CardContent className="p-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                            <Lock className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1 hover:no-underline flex-wrap">
                                                                <h3 className="font-semibold text-sm tracking-tight block hover:no-underline">{secret.name}</h3>
                                                                <Badge variant="secondary" className="text-[10px] h-5">{secret.type || "Secret"}</Badge>
                                                                <Badge variant="outline" className="text-[10px] h-5">{secret.isManagedSecret || "User Managed"}</Badge>
                                                            </div>
                                                            <div className="text-[11px] text-muted-foreground mb-2 break-all leading-relaxed">
                                                                <span className="font-medium text-foreground/80">Path:</span> {getDisplayValue(secret.fullName || secret.name)}
                                                            </div>
                                                            {renderSecretCardMeta(secret)}
                                                            <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                                                {"keyNames" in secret && Array.isArray(secret.keyNames) && secret.keyNames.length > 0 && (
                                                                    secret.keyNames.slice(0, 3).map((keyName: string) => (
                                                                        <Badge key={keyName} variant="outline" className="text-[10px] h-5">
                                                                            {keyName}
                                                                        </Badge>
                                                                    ))
                                                                )}
                                                                {Array.isArray(secret.keyNames) && secret.keyNames.length > 3 && (
                                                                    <Badge variant="outline" className="text-[10px] h-5">
                                                                        +{secret.keyNames.length - 3} more
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                            <div className="text-xs text-muted-foreground">Activity</div>
                                                            <ActivityBar count={secret.activity} className="w-20" />
                                                            <div className="max-w-[120px] text-right text-[11px] text-muted-foreground leading-snug">
                                                                Last activity: {getDisplayValue(secret.lastActivity || secret.lastModified)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 pt-3 border-t flex justify-end" onClick={(e) => e.stopPropagation()}>
                                                        <DetailsDialog data={secret} title={`Secret: ${secret.name}`} />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )
                                    })
                                )}
                            </TabsContent>
                        </Tabs>
                    </div>
                )}

                {/* Kubernetes NHI Content */}
                {platformTab === "kubernetes" && (
                    <div className="space-y-4">

                        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">

                            <TabsContent value="service-accounts" className="space-y-2">
                                {filteredK8sServiceAccounts.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-muted-foreground">No service accounts found</p>
                                    </div>
                                ) : (
                                    filteredK8sServiceAccounts.map((sa) => {
                                        let nhiLink = getK8sNHILink(sa, activeTab);
                                        return (
                                            <Card key={sa.id} className="hover-elevate cursor-pointer transition-transform hover:scale-[1.01]" data-testid={`card-k8s-sa-${sa.id}`}>
                                                <CardContent className="p-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                            <Shield className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">

                                                            <h3 className="font-semibold text-sm truncate mb-2 block hover:no-underline" onClick={() =>
                                                                navigate(nhiLink.pathname, { state: nhiLink.state })
                                                            }>{sa.name}</h3>
                                                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-2">

                                                                <div>
                                                                    <span className="text-muted-foreground">Namespace:</span> <span className="font-medium">{sa.namespace}</span>
                                                                </div>

                                                                <div>
                                                                    <span className="text-muted-foreground">Cluster:</span> <span className="font-medium">{sa.clusterId}</span>
                                                                </div>
                                                                <div className="col-span-2">
                                                                    <span className="text-muted-foreground">Synced:</span> {sa.syncedAt}
                                                                </div>
                                                            </div>
                                                            {sa.tags && sa.tags.length > 0 && (
                                                                <div className="flex flex-wrap gap-1">
                                                                    {sa.tags.map((tag: any, idx: any) => (
                                                                        <Badge key={idx} variant="outline" className="text-xs h-5 px-2">
                                                                            {tag}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                            <div className="text-xs text-muted-foreground">Activity</div>
                                                            <ActivityBar count={sa.activity} className="w-20" />
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 pt-3 border-t flex justify-end">
                                                        <DetailsDialog data={sa} title={`K8s Service Account: ${sa.name}`} />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )
                                    })
                                )}
                            </TabsContent>

                            <TabsContent value="tokens" className="space-y-2">
                                {filteredK8sTokens.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-muted-foreground">No tokens found</p>
                                    </div>
                                ) : (
                                    filteredK8sTokens.map((token) => {
                                        let nhiLink = getK8sNHILink(token, activeTab);

                                        return (

                                            <Card key={token.id} className="hover-elevate cursor-pointer transition-transform hover:scale-[1.01]" data-testid={`card-k8s-token-${token.id}`}>
                                                <CardContent className="p-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                            <Key className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-2">

                                                                <h3 className="font-semibold text-sm truncate block hover:no-underline" onClick={() =>
                                                                    navigate(nhiLink.pathname, { state: nhiLink.state })
                                                                }>{token.name}</h3>
                                                                <Badge variant="secondary" className="text-xs h-5">{token.type}</Badge>

                                                            </div>
                                                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                                                <div>
                                                                    <span className="text-muted-foreground">Namespace:</span> <span className="font-medium">{token.namespace}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-muted-foreground">Cluster:</span> <span className="font-medium">{token.clusterId}</span>
                                                                </div>
                                                                <div className="col-span-2">
                                                                    <span className="text-muted-foreground">Synced:</span> {token.syncedAt}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                            <div className="text-xs text-muted-foreground">Activity</div>
                                                            <ActivityBar count={token.activity} className="w-20" />
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 pt-3 border-t flex justify-end">
                                                        <DetailsDialog data={token} title={`K8s Token: ${token.name}`} />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )
                                    })
                                )}
                            </TabsContent>

                            <TabsContent value="secrets" className="space-y-2">
                                {filteredK8sSecrets.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-muted-foreground">No secrets found</p>
                                    </div>
                                ) : (
                                    filteredK8sSecrets.map((secret) => {
                                        let nhiLink = getK8sNHILink(secret, activeTab);

                                        return (
                                            <Card
                                                key={secret.id}
                                                className="hover-elevate cursor-pointer transition-transform hover:scale-[1.01]"
                                                data-testid={`card-k8s-secret-${secret.id}`}
                                                onClick={() => navigate(nhiLink.pathname, { state: nhiLink.state })}
                                            >
                                                <CardContent className="p-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                            <Lock className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                                <h3 className="font-semibold text-sm tracking-tight block hover:no-underline">{secret.name}</h3>
                                                                <Badge variant="secondary" className="text-[10px] h-5">{secret.type}</Badge>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] mb-2 leading-relaxed">
                                                                <div>
                                                                    <span className="text-muted-foreground">Namespace:</span> <span className="font-medium">{secret.namespace}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-muted-foreground">Cluster:</span> <span className="font-medium">{secret.clusterId}</span>
                                                                </div>
                                                                <div className="col-span-2">
                                                                    <span className="text-muted-foreground">Synced:</span> {secret.syncedAt}
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-wrap gap-1.5 text-[11px]">
                                                                <span className="inline-flex items-center rounded-md bg-muted/70 px-2 py-1 text-muted-foreground">
                                                                    <span className="mr-1 font-medium text-foreground">Owner:</span> {getDisplayValue(secret.cloudResourceAccountId || secret.wsTenantName)}
                                                                </span>
                                                                <span className="inline-flex items-center rounded-md bg-muted/70 px-2 py-1 text-muted-foreground">
                                                                    <span className="mr-1 font-medium text-foreground">Type:</span> {getDisplayValue(secret.type)}
                                                                </span>
                                                                <span className="inline-flex items-center rounded-md bg-muted/70 px-2 py-1 text-muted-foreground">
                                                                    <span className="mr-1 font-medium text-foreground">Immutable:</span> {secret.immutable ? "Yes" : "No"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                            <div className="text-xs text-muted-foreground">Activity</div>
                                                            <ActivityBar count={secret.activity} className="w-20" />
                                                            <div className="max-w-[120px] text-right text-[11px] text-muted-foreground leading-snug">
                                                                Last activity: {getDisplayValue(secret.lastActivity || secret.lastActivityMessage || secret.syncedAt)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 pt-3 border-t flex justify-end" onClick={(e) => e.stopPropagation()}>
                                                        <DetailsDialog data={secret} title={`K8s Secret: ${secret.name}`} />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )
                                    })
                                )}
                            </TabsContent>
                        </Tabs>
                    </div>
                )}
            </div>
        </div>
    );
}
